/**
 * Panelin sunucu istemcisi.
 *
 * Oturum çerezle taşınıyor; jeton JavaScript'e hiç verilmiyor (httpOnly).
 * Bu, çalınan bir betiğin oturumu alıp götürmesini engelliyor.
 */

export class ApiHatasi extends Error {
  constructor(
    readonly durum: number,
    readonly kod: string,
    readonly detay?: unknown,
    /**
     * Cevabın tamamı.
     *
     * Bazı hatalar yalnızca bir kod değil, üstünde işlem yapılacak veri
     * de döndürüyor — çakışan randevuların listesi gibi. `detay` tek
     * başına yetmiyordu; gövdeyi taşımak, her hata için ayrı bir alan
     * uydurmaktan daha az sürpriz üretiyor.
     */
    readonly govde?: Record<string, unknown>,
  ) {
    super(kod)
  }
}

async function istek<T>(yol: string, secenek: RequestInit = {}): Promise<T> {
  const r = await fetch(yol, {
    ...secenek,
    credentials: 'same-origin',
    headers: { 'content-type': 'application/json', ...(secenek.headers ?? {}) },
  })
  const metin = await r.text()
  const govde = metin ? (JSON.parse(metin) as Record<string, unknown>) : {}
  if (!r.ok) {
    throw new ApiHatasi(r.status, String(govde['hata'] ?? 'bilinmeyen'), govde['detay'], govde)
  }
  return govde as T
}

export const api = {
  giris: (eposta: string, parola: string) =>
    istek<{ kullanici: Kullanici }>('/api/auth/giris', {
      method: 'POST',
      body: JSON.stringify({ eposta, parola }),
    }),
  cikis: () => istek<{ tamam: boolean }>('/api/auth/cikis', { method: 'POST' }),
  ben: () => istek<{ kullanici: Kullanici }>('/api/auth/ben'),
  oturumlar: () =>
    istek<{ oturumlar: Oturum[]; suanki: string }>('/api/auth/oturumlar'),
  digerleriniKapat: () =>
    istek<{ kapatilan: number }>('/api/auth/digerlerini-kapat', { method: 'POST' }),

  danisanlar: (ara?: string) =>
    istek<{ danisanlar: DanisanOzet[] }>(
      '/api/danisanlar' + (ara ? `?ara=${encodeURIComponent(ara)}` : ''),
    ),
  danisanEkle: (g: YeniDanisan) =>
    istek<{ danisan: { id: string; dosyaNo: string } }>('/api/danisanlar', {
      method: 'POST',
      body: JSON.stringify(g),
    }),
  danisan: (id: string) => istek<DanisanDosyasi>(`/api/danisanlar/${id}`),
  danisanGuncelle: (id: string, g: Partial<YeniDanisan>) =>
    istek<{ guncellendi: boolean }>(`/api/danisanlar/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(g),
    }),
  kisiEkle: (danisanId: string, g: YeniKisi) =>
    istek<{ kisi: { id: string } }>(`/api/danisanlar/${danisanId}/kisiler`, {
      method: 'POST',
      body: JSON.stringify(g),
    }),
  kisiGuncelle: (id: string, g: Partial<YeniKisi> & { arsiv?: boolean }) =>
    istek<{ guncellendi: boolean }>(`/api/kisiler/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(g),
    }),
  onamGeriCek: (id: string, sebep?: string) =>
    istek<{ geriCekildi: boolean; kapsam: string }>(`/api/onamlar/${id}/geri-cek`, {
      method: 'POST',
      body: JSON.stringify({ sebep }),
    }),
  onamDurumu: (id: string) => istek<OnamDurumu>(`/api/danisanlar/${id}/onam-durumu`),
  onamEkle: (id: string, g: YeniOnam) =>
    istek<{ onam: { id: string } }>(`/api/danisanlar/${id}/onamlar`, {
      method: 'POST',
      body: JSON.stringify(g),
    }),
  veliBaglantisi: (danisanId: string, kisiId: string) =>
    istek<{ jeton: string; gecerlilikSn: number }>(
      `/api/danisanlar/${danisanId}/veli-baglantisi`,
      { method: 'POST', body: JSON.stringify({ kisiId }) },
    ),
  olcumGecmisi: (id: string) =>
    istek<{ olcumler: OlcumOzet[] }>(`/api/danisanlar/${id}/olcumler`),

  gorevler: () => istek<{ gorevler: GorevSatiri[] }>('/api/gorevler'),
  olcumBaslat: (danisanId: string, form: 'A' | 'B' | 'C', gorevler?: string[]) =>
    istek<{
      olcum: { id: string; form: string; gorevler: string[] | null; tahminiDakika: number }
      oynatma: { jeton: string; gecerlilikSn: number; salt: string }
    }>('/api/olcumler', {
      method: 'POST',
      body: JSON.stringify({ danisanId, form, ...(gorevler ? { gorevler } : {}) }),
    }),
  olcum: (id: string) => istek<OlcumSonucu>(`/api/olcumler/${id}`),
  denemeler: (olcumId: string, gorev: string, atla = 0, adet = 200) =>
    istek<{ denemeler: Deneme[]; toplam: number; atla: number; adet: number }>(
      `/api/olcumler/${olcumId}/denemeler?gorev=${encodeURIComponent(gorev)}&atla=${atla}&adet=${adet}`,
    ),

  programOnerisi: (danisanId: string, olcumId?: string) =>
    istek<ProgramOnerisi>(
      `/api/danisanlar/${danisanId}/program-onerisi` +
        (olcumId ? `?olcumId=${olcumId}` : ''),
    ),
  programlar: (danisanId: string) =>
    istek<{ programlar: ProgramOzet[] }>(`/api/danisanlar/${danisanId}/programlar`),
  programOlustur: (g: YeniProgram) =>
    istek<{ program: { id: string; hafta: number; odev: number } }>('/api/programlar', {
      method: 'POST',
      body: JSON.stringify(g),
    }),

  /**
   * Bu ayarlarla haftada kaç egzersiz çıkacak.
   *
   * Sunucuda, programı üreten İŞLEVİN KENDİSİYLE hesaplanıyor; panelde
   * ikinci bir kopya tutmak iki yerin zamanla ayrışması demekti.
   */
  programOnizleme: (g: Record<string, unknown>) =>
    istek<ProgramOnizleme>('/api/programlar/onizleme', {
      method: 'POST',
      body: JSON.stringify(g),
    }),

  egzersizKatalogu: (duzey: number) =>
    istek<{ katalog: KatalogSatiri[]; duzey: number }>(`/api/egzersiz-katalogu?duzey=${duzey}`),
  odevAta: (g: EldenAtama) =>
    istek<{ atama: { programId: string; adet: number } }>('/api/odevler', {
      method: 'POST',
      body: JSON.stringify(g),
    }),
  odevKaldir: (id: string) =>
    istek<{ kaldirildi: boolean }>(`/api/odevler/${id}`, { method: 'DELETE' }),

  odevler: (danisanId: string) =>
    istek<{ odevler: OdevOzet[] }>(`/api/danisanlar/${danisanId}/odevler`),
  odev: (id: string) => istek<OdevDetayi>(`/api/odevler/${id}`),
  odevBaglantisi: (id: string) =>
    istek<{ jeton: string; gecerlilikSn: number }>(`/api/odevler/${id}/baglanti`, {
      method: 'POST',
    }),

  raporlar: (danisanId: string) =>
    istek<{ raporlar: RaporOzet[] }>(`/api/danisanlar/${danisanId}/raporlar`),
  raporUret: (danisanId: string, hedef: RaporHedefi, donemBas: string, donemBit: string) =>
    istek<{ rapor: { id: string } }>('/api/raporlar', {
      method: 'POST',
      body: JSON.stringify({ danisanId, hedef, donemBas, donemBit }),
    }),
  rapor: (id: string) => istek<RaporDosyasi>(`/api/raporlar/${id}`),
  raporKaydet: (id: string, icerik: RaporIcerik) =>
    istek<{ duzeltme: RaporFarki }>(`/api/raporlar/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({ icerik }),
    }),
  raporGonder: (id: string, kisiId?: string) =>
    istek<{ gonderildi: boolean }>(`/api/raporlar/${id}/gonder`, {
      method: 'POST',
      body: JSON.stringify({ kisiId }),
    }),

  belgeAyarlari: () => istek<{ ayarlar: BelgeAyarlari }>('/api/belge-ayarlari'),
  belgeAyarKaydet: (g: Partial<BelgeAyarlari>) =>
    istek<{ guncellendi: boolean }>('/api/belge-ayarlari', {
      method: 'PATCH',
      body: JSON.stringify(g),
    }),
  belgeHesap: (g: {
    tur: string
    brut?: number
    tahsilat?: number
    kdvOrani: number
    stopajOrani: number
  }) =>
    istek<{ hesap: BelgeHesabi; yuvarlamaFarki: number }>('/api/belge-hesap', {
      method: 'POST',
      body: JSON.stringify(g),
    }),
  belgeler: (danisanId: string) =>
    istek<{ belgeler: BelgeOzet[] }>(`/api/danisanlar/${danisanId}/belgeler`),
  belgeOlustur: (danisanId: string, g: YeniBelge) =>
    istek<{ belge: { id: string } }>(`/api/danisanlar/${danisanId}/belgeler`, {
      method: 'POST',
      body: JSON.stringify(g),
    }),
  belgeKesildi: (id: string, belgeNo: string) =>
    istek<{ kesildi: boolean }>(`/api/belgeler/${id}/kesildi`, {
      method: 'POST',
      body: JSON.stringify({ belgeNo }),
    }),
  belgeIptal: (id: string, sebep?: string) =>
    istek<{ iptal: boolean }>(`/api/belgeler/${id}/iptal`, {
      method: 'POST',
      body: JSON.stringify({ sebep }),
    }),

  paketler: (hepsi = false) =>
    istek<{ paketler: Paket[] }>(`/api/paketler${hepsi ? '?hepsi=1' : ''}`),
  paketEkle: (g: YeniPaket) =>
    istek<{ paket: { id: string } }>('/api/paketler', {
      method: 'POST',
      body: JSON.stringify(g),
    }),
  paketGuncelle: (id: string, g: Partial<YeniPaket> & { aktif?: boolean; arsiv?: boolean }) =>
    istek<{ guncellendi: boolean }>(`/api/paketler/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(g),
    }),

  hesap: (danisanId: string) => istek<Hesap>(`/api/danisanlar/${danisanId}/hesap`),
  paketSat: (danisanId: string, g: PaketSatis) =>
    istek<{ paket: { id: string } }>(`/api/danisanlar/${danisanId}/paketler`, {
      method: 'POST',
      body: JSON.stringify(g),
    }),
  satisArsiv: (satisId: string, arsiv: boolean) =>
    istek<{ tamam: boolean }>(`/api/satis/${satisId}/arsiv`, {
      method: 'POST',
      body: JSON.stringify({ arsiv }),
    }),
  odemeEkle: (danisanId: string, g: YeniOdeme) =>
    istek<{ odeme: { id: string } }>(`/api/danisanlar/${danisanId}/odemeler`, {
      method: 'POST',
      body: JSON.stringify(g),
    }),
  odemeArsiv: (id: string) =>
    istek<{ arsivlendi: boolean }>(`/api/odemeler/${id}/arsiv`, { method: 'POST' }),

  seansKredisi: (seansId: string) =>
    istek<{ kredi: { satisId: string; paketAdi: string } | null }>(
      `/api/seanslar/${seansId}/kredi`,
    ),
  krediDus: (seansId: string, satisId: string) =>
    istek<{ dusuldu: boolean }>(`/api/seanslar/${seansId}/kredi`, {
      method: 'POST',
      body: JSON.stringify({ satisId }),
    }),
  krediIade: (seansId: string) =>
    istek<{ iade: boolean }>(`/api/seanslar/${seansId}/kredi`, { method: 'DELETE' }),

  tahsilatOzeti: (bas: string, bit: string) =>
    istek<{ ozet: TahsilatSatiri[] }>(
      `/api/tahsilat-ozeti?bas=${encodeURIComponent(bas)}&bit=${encodeURIComponent(bit)}`,
    ),

  notSablonlari: () => istek<{ sablonlar: Record<string, NotSablonu> }>('/api/not-sablonlari'),
  seanslar: (danisanId: string) =>
    istek<{ seanslar: SeansOzet[] }>(`/api/danisanlar/${danisanId}/seanslar`),
  seansAc: (g: {
    danisanId: string
    tur: string
    randevuId?: string
    baslangic?: string
    dakika?: number
  }) =>
    istek<{ seans: { id: string } }>('/api/seanslar', {
      method: 'POST',
      body: JSON.stringify(g),
    }),
  seans: (id: string) => istek<SeansDosyasi>(`/api/seanslar/${id}`),
  notKaydet: (seansId: string, g: { sablon: string; alanlar: Record<string, string>; metin: string }) =>
    istek<{ not: { notId: string; surum: number } }>(`/api/seanslar/${seansId}/not`, {
      method: 'PUT',
      body: JSON.stringify(g),
    }),
  notKilitle: (notId: string) =>
    istek<{ kilitlendi: boolean }>(`/api/notlar/${notId}/kilitle`, { method: 'POST' }),
  notEk: (notId: string, metin: string) =>
    istek<{ ek: { id: string } }>(`/api/notlar/${notId}/ek`, {
      method: 'POST',
      body: JSON.stringify({ metin }),
    }),
  notSurumleri: (notId: string) =>
    istek<{ surumler: NotSurumu[] }>(`/api/notlar/${notId}/surumler`),

  yerler: () => istek<Yerler>('/api/yerler'),
  subeEkle: (ad: string, adres?: string) =>
    istek<{ sube: { id: string } }>('/api/subeler', {
      method: 'POST',
      body: JSON.stringify({ ad, adres }),
    }),
  odaEkle: (subeId: string, ad: string) =>
    istek<{ oda: { id: string } }>('/api/odalar', {
      method: 'POST',
      body: JSON.stringify({ subeId, ad }),
    }),

  randevular: (bas: string, bit: string, uygulayiciId?: string, danisanId?: string) => {
    const q = new URLSearchParams({ bas, bit })
    if (uygulayiciId) q.set('uygulayiciId', uygulayiciId)
    if (danisanId) q.set('danisanId', danisanId)
    return istek<{ randevular: Randevu[] }>(`/api/randevular?${q}`)
  },
  randevuCakisma: (g: {
    uygulayiciId: string
    odaId?: string | null
    baslangic: string
    bitis: string
    haricId?: string
  }) =>
    istek<{ cakisanlar: Cakisan[] }>('/api/randevular/cakisma', {
      method: 'POST',
      body: JSON.stringify(g),
    }),
  randevuEkle: (g: YeniRandevu) =>
    istek<{ randevular: { id: string; baslangic: string }[]; seriId: string | null }>(
      '/api/randevular',
      { method: 'POST', body: JSON.stringify(g) },
    ),
  randevuTasi: (id: string, g: Record<string, unknown>) =>
    istek<{ tasindi: boolean }>(`/api/randevular/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(g),
    }),
  randevuDurum: (id: string, durum: string, sebep?: string, seriKalani = false) =>
    istek<{ tamam: boolean; seriIptal: number }>(`/api/randevular/${id}/durum`, {
      method: 'POST',
      body: JSON.stringify({ durum, sebep, seriKalani }),
    }),

  kullanicilar: () => istek<EkipDurumu>('/api/kullanicilar'),
  kullaniciEkle: (g: YeniKullanici) =>
    istek<{ kullanici: { id: string } }>('/api/kullanicilar', {
      method: 'POST',
      body: JSON.stringify(g),
    }),
  kullaniciDurum: (id: string, durum: 'active' | 'suspended') =>
    istek<{ kullanici: { id: string } }>(`/api/kullanicilar/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({ durum }),
    }),
  kullaniciArsivle: (id: string) =>
    istek<{ arsivlendi: string }>(`/api/kullanicilar/${id}`, { method: 'DELETE' }),
}

/* ------------------------------------------------------------------ */

export interface Kullanici {
  id: string
  adSoyad: string
  unvan?: string | null
  rol: string
  eposta?: string
}

export interface Oturum {
  id: string
  cihaz: string | null
  ip: string | null
  tarayici: string | null
  acilis: string
  sonGorulme: string
}

export interface DanisanOzet {
  id: string
  dosyaNo: string
  ad: string
  soyad: string
  dogumTarihi: string
  durum: string
}

export interface YeniDanisan {
  dosyaNo: string
  ad: string
  soyad: string
  dogumTarihi: string
  okul?: string
  sinif?: string
  telefon?: string
  yonlendiren?: string
  onDegerlendirme?: string
}

export interface YeniKisi {
  yakinlik: string
  adSoyad: string
  telefon?: string | null
  eposta?: string | null
  portal?: boolean
  yasalTemsilci?: boolean
}

export interface DanisanDosyasi {
  danisan: Record<string, unknown>
  ilgiliKisiler: Record<string, unknown>[]
  onamlar: {
    id: string
    kapsam: string
    belge: string
    surum: string
    verildi: string
    geriCekildi: string | null
    /** Islak imzalı belgenin taraması bağlı mı */
    kanitEkId: string | null
  }[]
}

export interface OnamDurumu {
  veriIsleme: boolean
  olcum: boolean
  program: boolean
  veliPortali: boolean
  /** Okulla paylaşım: öğretmen ataması buna bağlı */
  okulPaylasimi: boolean
  olcumBaslatilabilir: boolean
}

export interface YeniOnam {
  kapsam:
    | 'data_processing'
    | 'assessment'
    | 'program'
    | 'parent_portal'
    | 'school_sharing'
    | 'research'
  belgeKodu: string
  belgeSurumu: string
  yontem: 'wet_signature' | 'touch_signature' | 'sms_code'
  /** Onamı veren ilgili kişi (veli/vasi); boşsa danışanın kendisi */
  verenKisiId?: string
  kendiVerdi?: boolean
}

export interface OlcumOzet {
  id: string
  form: string
  durum: string
  baslangic: string
  bitis: string | null
  uyari: string | null
}

export interface OlcumSonucu {
  olcum: {
    id: string
    clientId: string
    form: string
    mode: string
    status: string
    instrumentCode: string
    instrumentVersion: string
    startedAt: string
    completedAt: string | null
    invalidatedReason: string | null
    measuredRefreshHz: string | null
    device: Record<string, unknown>
  }
  skorlar: {
    domain: string
    taskCode: string | null
    metric: string
    /** Postgres numeric'i JSON'a metin olarak gelir; sayıya çevirmek arayüzün işi */
    value: string
    unit: string
    trialsUsed: number
    scoringVersion: string
  }[]
  denemeSayisi: number
}

export interface Deneme {
  gorev: string
  alan: string
  sira: number
  duzey: number
  hedefMi: boolean
  tepkiMs: number | null
  sonuc: string | null
  gecerli: boolean
  gecersizlikSebebi: string | null
  uyarici: Record<string, unknown> | null
  cevap: string | string[] | null
  beklenen: string | string[] | null
}

export const SONUC_ADI: Record<string, string> = {
  hit: 'Doğru bastı',
  miss: 'Kaçırdı',
  false_alarm: 'Yanlış bastı',
  correct_rejection: 'Doğru bekledi',
  omission: 'Cevap vermedi',
}

/**
 * Geçersizlik sebepleri.
 *
 * `anticipation` burada bilerek "teknik arıza" dilinde yazılmıyor:
 * uyarıcıdan önce basmak davranışsal bir bulgu, dürtüsellik göstergesi.
 * Uygulayıcının bunu ekran sorunlarıyla karıştırmaması gerekiyor.
 */
export const GECERSIZLIK_ADI: Record<string, string> = {
  anticipation: 'uyarıcıdan önce bastı (dürtüsellik)',
  too_fast: 'insan tepkisi için çok hızlı',
  frames_dropped: 'ekran kare atladı',
  presentation_stalled: 'ekran dondu',
  tab_hidden: 'sekme arkaya düştü',
  input_lost: 'giriş kaydedilemedi',
  manual: 'elle geçersiz sayıldı',
}

export type Alan =
  | 'sustained_attention'
  | 'selective_attention'
  | 'working_memory_visual'
  | 'working_memory_verbal'
  | 'processing_speed'
  | 'inhibition'
  | 'cognitive_flexibility'

export const ALAN_ADI: Record<string, string> = {
  sustained_attention: 'Dikkati sürdürme',
  selective_attention: 'Seçici dikkat',
  working_memory_visual: 'Görsel işleyen bellek',
  working_memory_verbal: 'Sözel işleyen bellek',
  processing_speed: 'İşlem hızı',
  inhibition: 'Dürtü kontrolü',
  cognitive_flexibility: 'Bilişsel esneklik',
}

export const ALANLAR: Alan[] = [
  'sustained_attention',
  'inhibition',
  'selective_attention',
  'working_memory_visual',
  'working_memory_verbal',
  'processing_speed',
  'cognitive_flexibility',
]

export interface ProgramOnerisi {
  temelOlcumId: string | null
  odak: Partial<Record<Alan, number>>
  gerekce: { domain: Alan; kural: string; aciklama: string; agirlik: number }[]
  notrOlcum: boolean
  seviye: number
  olcumVar: boolean
}

export interface ProgramOzet {
  id: string
  hafta: number
  odak: Record<string, number>
  durum: string
  baslangic: string | null
  degistirildi: boolean
  temelOlcumId: string | null
}

export interface YeniProgram {
  danisanId: string
  haftaSayisi: number
  haftalikDakika: number
  kanal: 'digital' | 'print' | 'both'
  baslangic: string
  seviye?: number
  odak?: Partial<Record<Alan, number>>
  temelOlcumId?: string
}

export interface KatalogSatiri {
  kod: string
  ad: string
  alanlar: string[]
  kanallar: string[]
  saniye: number | null
  maddeSayisi: number | null
}

export interface EldenAtama {
  danisanId: string
  sonTarih?: string
  egzersizler: { kod: string; duzey: number; kanal: 'digital' | 'print'; adet: number }[]
}

export interface GorevSatiri {
  kod: string
  ad: string
  alanlar: string[]
  saniye: number
  denemeSayisi: number
}

export interface OdevOzet {
  id: string
  kod: string
  surum: string
  alan: string
  duzey: number
  kanal: string
  sonTarih: string | null
  hafta: number
  haftaId: string
  yapildi: string | null
  dogru: number | null
  toplam: number | null
  sureMs: number | null
}

export interface OdevMaddesi {
  id: string
  beklenen: string
  verilen: string | null
  dogru: boolean
  fazladan?: boolean
}

export interface OdevDetayi {
  odev: {
    id: string
    kod: string
    surum: string
    alan: string
    duzey: number
    kanal: string
    hafta: number
    sonTarih: string | null
    yonerge: string | null
  }
  sonuc: {
    yapildi: string
    sureMs: number | null
    dogru: number
    toplam: number
    oran: string
    maddeler: OdevMaddesi[] | null
  } | null
}

/**
 * Haftanın kâğıt çıktısı.
 *
 * `anahtar=1` cevap anahtarını ekler ve bu ÇOCUĞA VERİLEN çıktıda
 * olmamalı; bu yüzden ayrı bir bağlantı.
 */
export function haftaBaskiAdresi(haftaId: string, anahtar = false): string {
  return `/api/haftalar/${haftaId}/baski${anahtar ? '?anahtar=1' : ''}`
}

/**
 * Kardeş uygulamaların adresi.
 *
 * Panel artık kökte değil `danisman.nirengi.…` altında; çocuk oynatıcısı
 * `cocuk.nirengi.…`, veli portalı `veli.nirengi.…`. Ortak kök "nirengi"
 * etiketinden itibaren alınıyor.
 *
 * Önceki hali `hostname.startsWith('nirengi.')` diye bakıyordu ve panel
 * taşınınca ana makine adı artık "nirengi." ile başlamadığı için üretilen
 * bağlantılar sessizce localhost'u gösteriyordu — yani uygulayıcı çocuğa
 * açılmayan bir bağlantı verirdi.
 */
function kardesAlan(etiket: string): string | null {
  const h = location.hostname
  const i = h.indexOf('nirengi.')
  // "nirengi." bir ETİKETİN başı olmalı; "xnirengi." eşleşmemeli
  if (i !== 0 && !(i > 0 && h[i - 1] === '.')) return null
  return `${location.protocol}//${etiket}.${h.slice(i)}`
}

/**
 * Haftanın tamamının adresi.
 *
 * Çocuk tek kod okutup o haftanın egzersizlerini listeden seçiyor.
 * Ödev başına bağlantı üretmek, dört egzersizlik bir hafta için dört
 * kod okutmak demekti ve pratikte uygulayıcı tableti alıp kendi
 * açıyordu.
 */
export function haftaAdresi(jeton: string): string {
  const taban =
    (import.meta.env['VITE_OYNATICI'] as string | undefined) ??
    kardesAlan('cocuk') ??
    'http://localhost:5174'
  return `${taban}/?h=${encodeURIComponent(jeton)}`
}

/** Egzersiz oynatıcısının adresi — ölçümle aynı mantık, farklı parametre */
export function odevAdresi(jeton: string): string {
  const taban =
    (import.meta.env['VITE_OYNATICI'] as string | undefined) ??
    kardesAlan('cocuk') ??
    'http://localhost:5174'
  return `${taban}/?e=${encodeURIComponent(jeton)}`
}

/**
 * Egzersiz adları MOTORDAN geliyor.
 *
 * Burada bir kopyası vardı; çocuk uygulamasına ikinci bir kopya
 * yazıldığında yarısı eksik kaldı ve tablette ekranda ham kod
 * göründü. Tek kaynak motorda (engine/exercises/types.ts) ve tipi
 * yeni bir kodun adsız kalmasını derleme hatası yapıyor.
 */
export { EGZERSIZ_ADI, egzersizAdi } from '@nirengi/engine'

export type RaporHedefi = 'parent' | 'clinician'

export interface RaporOzet {
  id: string
  hedef: string
  donemBas: string
  donemBit: string
  uretildi: string
  gonderildi: string | null
  duzeltme: { fark?: RaporFarki } | null
}

export interface RaporIcerik {
  surum: '1'
  hedef: RaporHedefi
  ozet: string
  bolumler: { anahtar: string; baslik: string; metin: string }[]
  kisitlar: string
}

export interface RaporFarki {
  ozet: boolean
  bolumler: string[]
}

export interface RaporDosyasi {
  rapor: {
    id: string
    danisanId: string
    hedef: RaporHedefi
    donemBas: string
    donemBit: string
    uretildi: string
    gonderildi: string | null
    gonderilenKisiId: string | null
    duzeltme: { fark?: RaporFarki } | null
    icerik: RaporIcerik
  }
  ilgiliKisiler: { id: string; adSoyad: string; yakinlik: string; portal: boolean }[]
}

export const YAKINLIK_ADI: Record<string, string> = {
  mother: 'Anne',
  father: 'Baba',
  guardian: 'Vasi',
  teacher: 'Öğretmen',
  physician: 'Hekim',
  other: 'Diğer',
}

export const HEDEF_ADI: Record<string, string> = {
  parent: 'Veli',
  clinician: 'Klinik',
  institution: 'Kurum',
}

export interface BelgeAyarlari {
  belgeTuru: 'smm' | 'fatura'
  unvan: string | null
  vergiNo: string | null
  vergiDairesi: string | null
  adres: string | null
  kdvOrani: string | null
  stopajOrani: string | null
  saglayici?: string
  hatirlatmaAcik: boolean
  hatirlatmaSaat: number
  odevHatirlatmaGun: number
}

export interface BelgeHesabi {
  brut: number
  kdvOrani: number
  kdv: number
  stopajOrani: number
  stopaj: number
  net: number
  tahsilat: number
}

export interface BelgeOzet {
  id: string
  tur: string
  belgeNo: string | null
  alici: string
  brut: string | null
  kdv: string | null
  stopaj: string | null
  tahsilat: string
  paraBirimi: string
  durum: string
  kesildi: string | null
  olusturuldu: string
  odemeId: string | null
  not: string | null
}

export interface YeniBelge {
  tur?: 'smm' | 'fatura'
  aliciAdi: string
  aliciVergiNo?: string | null
  aliciAdres?: string | null
  brut: string
  kdvOrani: number
  stopajOrani: number
  paraBirimi: string
  odemeId?: string | null
  not?: string
}

/* ---------------------------- Okul tarafı ------------------------- */

export const ogretmenApi = {
  liste: (ara?: string, tur?: OgretmenTuru) =>
    istek<{ ogretmenler: OgretmenSatiri[]; turAdi: Record<string, string> }>(
      '/api/ogretmenler' +
        (ara || tur
          ? '?' + new URLSearchParams({ ...(ara ? { ara } : {}), ...(tur ? { tur } : {}) })
          : ''),
    ),
  ekle: (g: YeniOgretmen) =>
    istek<{ ogretmen: { id: string } }>('/api/ogretmenler', {
      method: 'POST',
      body: JSON.stringify(g),
    }),
  guncelle: (id: string, g: Partial<YeniOgretmen>) =>
    istek<{ guncellendi: boolean }>(`/api/ogretmenler/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(g),
    }),
  arsivle: (id: string) =>
    istek<{ arsivlendi: boolean }>(`/api/ogretmenler/${id}/arsivle`, { method: 'POST' }),
  detay: (id: string) => istek<OgretmenDetayi>(`/api/ogretmenler/${id}`),

  danisanin: (danisanId: string) =>
    istek<{
      ogretmenler: DosyaOgretmeni[]
      okulOnayi: boolean
      turAdi: Record<string, string>
    }>(`/api/danisanlar/${danisanId}/ogretmenler`),
  ata: (ogretmenId: string, danisanId: string) =>
    istek<{ atama: { id: string } }>('/api/atamalar', {
      method: 'POST',
      body: JSON.stringify({ ogretmenId, danisanId }),
    }),
  atamaKaldir: (atamaId: string) =>
    istek<{ kaldirildi: boolean }>(`/api/atamalar/${atamaId}/kaldir`, { method: 'POST' }),
  notKaydet: (atamaId: string, not: string) =>
    istek<{ kaydedildi: boolean }>(`/api/atamalar/${atamaId}/not`, {
      method: 'PUT',
      body: JSON.stringify({ not }),
    }),
}

export type OgretmenTuru = 'teacher' | 'counselor'

export const OGRETMEN_TURU_ADI: Record<OgretmenTuru, string> = {
  teacher: 'Öğretmen',
  counselor: 'Rehber öğretmen',
}

/** Hangi adresten girdikleri — ekranda söylenmesi gerekiyor */
export const OGRETMEN_ADRESI: Record<OgretmenTuru, string> = {
  teacher: 'ogretmen',
  counselor: 'rehber',
}

export interface YeniOgretmen {
  adSoyad: string
  tur: OgretmenTuru
  okul?: string
  brans?: string
  eposta: string
  telefon?: string
  icNot?: string
}

export interface OgretmenSatiri {
  id: string
  adSoyad: string
  tur: OgretmenTuru
  okul: string | null
  brans: string | null
  eposta: string
  telefon: string | null
  arsiv: string | null
  ogrenci: number
}

export interface OgretmenDetayi {
  ogretmen: {
    id: string
    adSoyad: string
    tur: OgretmenTuru
    turAdi: string
    formKodu: string
    okul: string | null
    brans: string | null
    eposta: string
    telefon: string | null
    icNot: string | null
    arsiv: string | null
  }
  ogrenciler: {
    atamaId: string
    danisanId: string
    ad: string
    soyad: string
    not: string | null
    notTarih: string | null
    atandi: string
  }[]
}

export interface DosyaOgretmeni {
  atamaId: string
  ogretmenId: string
  adSoyad: string
  tur: OgretmenTuru
  okul: string | null
  brans: string | null
  eposta: string
  not: string | null
  notTarih: string | null
  atandi: string
}

/* ------------------------- Gözlem formları ------------------------ */

export const gozlemApi = {
  liste: (danisanId: string) =>
    istek<{ formlar: GozlemSatiri[]; bekleyenler: GozlemBekleyen[] }>(
      `/api/danisanlar/${danisanId}/gozlemler`,
    ),
  tanimlar: () =>
    istek<{ formlar: Record<string, GozlemTanimi>; surum: string }>('/api/gozlem-formlari'),
  iste: (danisanId: string, g: GozlemIstegi) =>
    istek<{ istek: { id: string; kod: string }; gecerlilikGun?: number }>(
      `/api/danisanlar/${danisanId}/gozlemler`,
      { method: 'POST', body: JSON.stringify(g) },
    ),
  iptal: (istekId: string) =>
    istek<{ iptal: boolean }>(`/api/gozlem-istekleri/${istekId}/iptal`, { method: 'POST' }),
  form: (id: string) =>
    istek<{ form: GozlemDolu; tanim: GozlemTanimi | null }>(`/api/gozlemler/${id}`),
  doldur: (danisanId: string, kod: string, cevaplar: Record<string, number>, istekId?: string) =>
    istek<{ form: { id: string; toplam: number } }>(
      `/api/danisanlar/${danisanId}/gozlemler/doldur`,
      { method: 'POST', body: JSON.stringify({ kod, cevaplar, istekId }) },
    ),
}

export interface GozlemIstegi {
  dolduran: 'parent' | 'teacher' | 'clinician'
  /** Öğretmen isteklerinde gönderilmiyor: öğretmenin türü belirliyor */
  kod?: string
  kisiId?: string
  ogretmenId?: string
  not?: string
}

export interface GozlemSatiri {
  id: string
  kod: string
  surum: string
  dolduran: string
  dolduranAdi: string | null
  toplam: string
  altlar: unknown
  tarih: string
}

export interface GozlemBekleyen {
  id: string
  kod: string
  dolduran: string
  kisi: string | null
  istendi: string
  bitis: string | null
  not: string | null
  ogretmenTuru: OgretmenTuru | null
}

export interface GozlemTanimi {
  kod: string
  ad: string
  aciklama: string
  secenekler: string[]
  altBasliklar: Record<string, string>
  maddeler: { anahtar: string; metin: string; alt: string; ters?: boolean }[]
}

export interface GozlemDolu {
  id: string
  kod: string
  surum: string
  dolduran: string
  dolduranAdi: string | null
  cevaplar: Record<string, number>
  toplam: string
  altlar: { altlar: Record<string, { puan: number; enCok: number }>; cevaplanan: number; madde: number }
  tarih: string
}

export const DOLDURAN_ADI: Record<string, string> = {
  parent: 'Veli',
  teacher: 'Öğretmen',
  clinician: 'Uygulayıcı',
  self: 'Danışanın kendisi',
}

/* ---------------------------- Dosya ekleri ------------------------ */

export const ekApi = {
  turler: () =>
    istek<{ turler: string[]; mimeler: string[]; enCokBayt: number }>('/api/ek-turleri'),
  liste: (danisanId: string) => istek<{ ekler: Ek[] }>(`/api/danisanlar/${danisanId}/ekler`),

  /**
   * Yükleme `fetch` ile FormData gönderiyor: content-type'ı TARAYICI
   * koyuyor, biz koymuyoruz. Elle `multipart/form-data` yazmak sınır
   * dizgesini (boundary) eksik bırakıyor ve sunucu gövdeyi hiç
   * ayrıştıramıyor.
   */
  yukle: async (danisanId: string, dosya: File, tur: string, ad?: string) => {
    const govde = new FormData()
    govde.append('tur', tur)
    if (ad) govde.append('ad', ad)
    govde.append('dosya', dosya)
    const r = await fetch(`/api/danisanlar/${danisanId}/ekler`, {
      method: 'POST',
      credentials: 'same-origin',
      body: govde,
    })
    const metin = await r.text()
    const cevap = metin ? (JSON.parse(metin) as Record<string, unknown>) : {}
    if (!r.ok) {
      throw new ApiHatasi(r.status, String(cevap['hata'] ?? 'bilinmeyen'), undefined, cevap)
    }
    return cevap as { ek: { id: string; ad: string; mime: string; boyut: number } }
  },

  arsivle: (ekId: string) =>
    istek<{ arsivlendi: boolean }>(`/api/ekler/${ekId}/arsivle`, { method: 'POST' }),
  indirmeYolu: (ekId: string) => `/api/ekler/${ekId}/indir`,
  kanitBagla: (onamId: string, ekId: string | null) =>
    istek<{ baglandi: boolean }>(`/api/onamlar/${onamId}/kanit`, {
      method: 'PUT',
      body: JSON.stringify({ ekId }),
    }),
}

export interface Ek {
  id: string
  tur: string
  ad: string
  mime: string
  boyut: number
  ozet: string
  eklendi: string
}

export const EK_TURU_ADI: Record<string, string> = {
  physician_report: 'Hekim raporu',
  school_letter: 'Okul yazısı',
  consent_scan: 'Onam taraması',
  exercise_scan: 'Egzersiz taraması',
  other: 'Diğer',
}

export const boyutYaz = (bayt: number): string =>
  bayt < 1024
    ? `${bayt} B`
    : bayt < 1024 * 1024
      ? `${(bayt / 1024).toFixed(0)} KB`
      : `${(bayt / 1024 / 1024).toFixed(1)} MB`

/* -------------------------- Davranış takibi ----------------------- */

export const davranisApi = {
  liste: (danisanId: string, kapali?: boolean) =>
    istek<{ hedefler: DavranisHedefi[] }>(
      `/api/danisanlar/${danisanId}/davranislar${kapali ? '?kapali=1' : ''}`,
    ),
  ekle: (danisanId: string, g: YeniDavranis) =>
    istek<{ hedef: { id: string } }>(`/api/danisanlar/${danisanId}/davranislar`, {
      method: 'POST',
      body: JSON.stringify(g),
    }),
  kapat: (hedefId: string) =>
    istek<{ kapatildi: boolean }>(`/api/davranislar/${hedefId}/kapat`, { method: 'POST' }),
  ac: (hedefId: string) =>
    istek<{ acildi: boolean }>(`/api/davranislar/${hedefId}/ac`, { method: 'POST' }),
  kayit: (hedefId: string, g: DavranisKaydi) =>
    istek<{ kaydedildi: boolean; hafta: string }>(`/api/davranislar/${hedefId}/kayit`, {
      method: 'PUT',
      body: JSON.stringify(g),
    }),
  kayitSil: (kayitId: string) =>
    istek<{ silindi: boolean }>(`/api/davranis-kayitlari/${kayitId}`, { method: 'DELETE' }),
}

export type DavranisYonu = 'lower' | 'higher'

export const DAVRANIS_YONU_ADI: Record<DavranisYonu, string> = {
  lower: 'Azalması iyi',
  higher: 'Artması iyi',
}

export interface YeniDavranis {
  metin: string
  birim: string
  yon: DavranisYonu
  baslangic?: number | null
  hedef?: number | null
}

export interface DavranisKaydi {
  hafta: string
  deger: number
  kaynak?: 'parent' | 'teacher' | 'clinician' | 'self'
  not?: string
}

export interface DavranisOkumasi {
  degisim: number | null
  iyilesme: boolean | null
  ilerleme: number | null
  hedefeUlasti: boolean | null
  cumle: string
}

export interface DavranisHedefi {
  id: string
  metin: string
  birim: string
  yon: DavranisYonu
  yonAdi: string
  baslangic: number | null
  hedef: number | null
  aktif: boolean
  kayitlar: { id: string; hafta: string; deger: number; kaynak: string; not: string | null }[]
  /** Kaynak başına ayrı okuma — ortalamaları ALINMIYOR */
  okumalar: Record<string, DavranisOkumasi>
}

/** Haftanın pazartesisi; sunucu da aynısını yapıyor ama ekran da göstersin */
export function haftaBasi(d: Date = new Date()): string {
  const x = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()))
  const isoGun = x.getUTCDay() === 0 ? 7 : x.getUTCDay()
  x.setUTCDate(x.getUTCDate() - (isoGun - 1))
  return x.toISOString().slice(0, 10)
}

export interface ProgramOnizleme {
  haftalar: {
    kanal: 'digital' | 'print'
    hafta: number
    enAz: number
    enCok: number
    dakika: number
    ornek: string[]
  }[]
  seviye: number
  odak: Record<string, number>
}

export const haftaApi = {
  baglanti: (haftaId: string) =>
    istek<{ jeton: string; hafta: number; gecerlilikSn: number }>(
      `/api/hafta/${haftaId}/baglanti`,
      { method: 'POST' },
    ),
}

/* ------------------------- Denetim ve KVKK ------------------------ */

export const denetimApi = {
  liste: (s: Record<string, string | number | undefined>) => {
    const q = new URLSearchParams()
    for (const [k, v] of Object.entries(s)) if (v !== undefined && v !== '') q.set(k, String(v))
    return istek<{ kayitlar: DenetimSatiri[] }>(`/api/denetim?${q}`)
  },
  danisan: (danisanId: string) =>
    istek<{ kayitlar: DosyaDenetimi[] }>(`/api/danisanlar/${danisanId}/denetim`),

  basvurular: (acik?: boolean) =>
    istek<{ basvurular: KvkkBasvurusu[]; turAdi: Record<string, string>; sureGun: number }>(
      `/api/kvkk-basvurulari${acik ? '?acik=1' : ''}`,
    ),
  basvuruEkle: (g: YeniBasvuru) =>
    istek<{ basvuru: { id: string; sonTarih: string } }>('/api/kvkk-basvurulari', {
      method: 'POST',
      body: JSON.stringify(g),
    }),
  sonuclandir: (id: string, sonuc: string) =>
    istek<{ sonuclandi: boolean }>(`/api/kvkk-basvurulari/${id}/sonuclandir`, {
      method: 'POST',
      body: JSON.stringify({ sonuc }),
    }),
  dokumYolu: (danisanId: string) => `/api/danisanlar/${danisanId}/dokum`,
}

export const EYLEM_ADI: Record<string, string> = {
  insert: 'Ekleme',
  update: 'Değişiklik',
  delete: 'Silme',
  read_sensitive: 'Dosya görüntüleme',
  export: 'Dışa aktarma',
  login: 'Giriş',
  login_failed: 'Başarısız giriş',
  logout: 'Çıkış',
}

export const TABLO_ADI: Record<string, string> = {
  client: 'Danışan dosyası',
  related_person: 'İlgili kişi',
  consent: 'Onam',
  attachment: 'Ek',
  appointment: 'Randevu',
  encounter: 'Görüşme',
  encounter_note: 'Seans notu',
  encounter_note_addendum: 'Not eki',
  assessment: 'Ölçüm',
  assessment_score: 'Ölçüm puanı',
  observation_form: 'Gözlem formu',
  observation_request: 'Gözlem isteği',
  behavior_target: 'Davranış hedefi',
  behavior_log: 'Davranış kaydı',
  teacher: 'Öğretmen',
  teacher_assignment: 'Öğretmen ataması',
  program: 'Program',
  program_week: 'Program haftası',
  exercise_assignment: 'Ödev',
  report: 'Rapor',
  payment: 'Ödeme',
  invoice: 'Belge',
  client_package: 'Paket',
  data_subject_request: 'KVKK başvurusu',
  audit_log: 'Denetim kaydı',
  app_user: 'Kullanıcı',
  branch: 'Şube',
  room: 'Oda',
}

export interface DenetimSatiri {
  id: string
  zaman: string
  eylem: string
  tablo: string | null
  satir: string | null
  kullaniciId: string | null
  kullanici: string | null
  kisiId: string | null
  ogretmenId: string | null
  yoneticiId: string | null
  degisen: unknown
}

export interface DosyaDenetimi {
  id: string
  zaman: string
  eylem: string
  tablo: string
  satir: string
  kullanici: string | null
  ogretmen: string | null
  kisi: string | null
  yoneticiId: string | null
  degisen: unknown
  ip: string | null
}

export interface KvkkBasvurusu {
  id: string
  tur: string
  basvuran: string
  danisanId: string | null
  ad: string | null
  soyad: string | null
  geldi: string
  sonTarih: string
  sonuclandi: string | null
  sonuc: string | null
  ilgilenen: string | null
}

export interface YeniBasvuru {
  tur: string
  basvuran: string
  danisanId?: string | null
  geldi?: string
}

/* -------------------------- Şube ve oda --------------------------- */

export const yerApi = {
  subeGuncelle: (id: string, g: { ad?: string; adres?: string | null; telefon?: string | null }) =>
    istek<{ guncellendi: boolean }>(`/api/subeler/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(g),
    }),
  subeArsivle: (id: string) =>
    istek<{ arsivlendi: boolean }>(`/api/subeler/${id}/arsivle`, { method: 'POST' }),
  odaGuncelle: (id: string, ad: string) =>
    istek<{ guncellendi: boolean }>(`/api/odalar/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({ ad }),
    }),
  odaArsivle: (id: string) =>
    istek<{ arsivlendi: boolean }>(`/api/odalar/${id}/arsivle`, { method: 'POST' }),
}

/* ------------------------ Hatırlatma durumu ----------------------- */

export const hatirlatmaApi = {
  liste: (s: Record<string, string | number | undefined>) => {
    const q = new URLSearchParams()
    for (const [k, v] of Object.entries(s)) if (v !== undefined && v !== '') q.set(k, String(v))
    return istek<{
      hatirlatmalar: HatirlatmaSatiri[]
      sayac: Record<string, number>
      sablonAdi: Record<string, string>
      durumAdi: Record<string, string>
    }>(`/api/hatirlatmalar?${q}`)
  },
  tekrar: (id: string) =>
    istek<{ kuyruga_alindi: boolean }>(`/api/hatirlatmalar/${id}/tekrar`, { method: 'POST' }),
  iptal: (id: string) =>
    istek<{ iptal: boolean }>(`/api/hatirlatmalar/${id}/iptal`, { method: 'POST' }),
}

export interface HatirlatmaSatiri {
  id: string
  sablon: string
  kanal: string
  durum: string
  planlanan: string
  gonderildi: string | null
  deneme: number
  sonrakiDeneme: string | null
  kalici: boolean
  hata: { mesaj?: string; zaman?: string; deneme?: number } | null
  alici: string | null
  aliciEposta: string | null
  danisanId: string | null
  ad: string | null
  soyad: string | null
}

export const KANAL_ADI: Record<string, string> = {
  email: 'E-posta',
  sms: 'SMS',
  whatsapp: 'WhatsApp',
  inapp: 'Uygulama içi',
}

export const BELGE_ADI: Record<string, string> = {
  smm: 'Serbest meslek makbuzu',
  fatura: 'Fatura',
}

export const BELGE_DURUMU: Record<string, string> = {
  pending: 'Taslak',
  issued: 'Kesildi',
  cancelled: 'İptal',
  failed: 'Hata',
}

export interface Paket {
  id: string
  ad: string
  seans: number
  fiyat: string
  paraBirimi: string
  gecerlilikGun: number | null
  aktif: boolean
}

export interface YeniPaket {
  ad: string
  seans: number
  fiyat: string
  paraBirimi: string
  gecerlilikGun?: number | null
}

export interface SatilanPaket {
  id: string
  ad: string
  tutar: string
  paraBirimi: string
  toplam: number
  kullanilan: number
  satis: string
  bitis: string | null
  not: string | null
  arsiv: string | null
}

export interface Odeme {
  id: string
  tutar: string
  paraBirimi: string
  yontem: string
  tarih: string
  not: string | null
  paketId: string | null
}

export interface Hesap {
  paketler: SatilanPaket[]
  odemeler: Odeme[]
  bakiye: Record<string, { borc: number; tahsil: number; kalan: number }>
}

export interface PaketSatis {
  paketId?: string
  ad?: string
  seans?: number
  tutar?: string
  paraBirimi?: string
  gecerlilikGun?: number | null
  not?: string
}

export interface YeniOdeme {
  tutar: string
  paraBirimi: string
  yontem: 'cash' | 'card' | 'transfer' | 'other'
  satisId?: string | null
  tarih?: string
  not?: string
}

export interface TahsilatSatiri {
  paraBirimi: string
  yontem: string
  tutar: number
  adet: number
}

export const ODEME_YONTEMI: Record<string, string> = {
  cash: 'Nakit',
  card: 'Kart',
  transfer: 'Havale/EFT',
  other: 'Diğer',
}

/** Tutarı para birimiyle yazar; biçim merkezin seçtiği birime göre */
export function paraYaz(tutar: string | number, paraBirimi: string): string {
  const n = Number(tutar)
  if (Number.isNaN(n)) return String(tutar)
  try {
    return n.toLocaleString('tr-TR', { style: 'currency', currency: paraBirimi })
  } catch {
    // Tanınmayan para birimi: merkez istediğini yazabiliyor
    return `${n.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ${paraBirimi}`
  }
}

export interface NotSablonu {
  ad: string
  alanlar: { anahtar: string; etiket: string; ipucu: string; satir: number }[]
}

export interface SeansOzet {
  id: string
  tur: string
  baslangic: string
  bitis: string | null
  uygulayici: string | null
  randevuId: string | null
  notId: string | null
  notSablonu: string | null
  kilitli: string | null
}

export interface SeansDosyasi {
  seans: {
    id: string
    danisanId: string
    tur: string
    baslangic: string
    bitis: string | null
    randevuId: string | null
  }
  not: {
    id: string
    sablon: string
    alanlar: Record<string, string>
    metin: string
    kilitli: string | null
    guncellendi: string
    surumSayisi: number
  } | null
  ekler: { id: string; metin: string; yazan: string | null; tarih: string }[]
}

export interface NotSurumu {
  surum: number
  alanlar: Record<string, string> | null
  metin: string | null
  tarih: string
  yazan: string | null
}

export const SEANS_TURU: Record<string, string> = {
  intake: 'İlk görüşme',
  followup: 'Takip',
  program: 'Program seansı',
  family: 'Aile görüşmesi',
  assessment: 'Ölçüm oturumu',
}

export interface Yerler {
  subeler: { id: string; ad: string; adres: string | null }[]
  odalar: { id: string; subeId: string; ad: string }[]
  uygulayicilar: { id: string; adSoyad: string; unvan: string | null }[]
}

export interface Randevu {
  id: string
  baslangic: string
  bitis: string
  durum: string
  iptalSebebi: string | null
  seriId: string | null
  danisanId: string
  danisanAd: string
  danisanSoyad: string
  uygulayiciId: string
  uygulayici: string | null
  odaId: string | null
  oda: string | null
}

export interface Cakisan {
  id: string
  baslangic: string
  danisanAd: string
  danisanSoyad: string
  sebep: 'oda' | 'uygulayici'
}

export interface YeniRandevu {
  danisanId: string
  uygulayiciId: string
  odaId?: string | null
  baslangic: string
  dakika: number
  tekrarHafta: number
  onayla?: boolean
}

export const RANDEVU_DURUMU: Record<string, string> = {
  scheduled: 'Planlandı',
  attended: 'Gelindi',
  no_show: 'Gelinmedi',
  cancelled: 'İptal',
}

export interface EkipDurumu {
  kullanicilar: {
    id: string
    adSoyad: string
    unvan: string | null
    eposta: string
    rol: string
    durum: string
    sonGiris: string | null
  }[]
  merkez: string
  koltuk: { limit: number; dolu: number }
}

export interface YeniKullanici {
  eposta: string
  adSoyad: string
  unvan?: string
  rol: 'practitioner' | 'assistant' | 'trainee'
  geciciParola: string
}

/**
 * Çocuğun cihazında açılacak adres.
 *
 * Oynatıcı ayrı bir alan adında duruyor (cocuk.nirengi…): panelin
 * oturum çerezi oraya gitmesin diye. Jeton adres çubuğunda taşınıyor,
 * başka yolu yok — bu yüzden tek kullanımlık ve kısa ömürlü.
 */
export function oynaticiAdresi(jeton: string): string {
  const taban =
    (import.meta.env['VITE_OYNATICI'] as string | undefined) ??
    kardesAlan('cocuk') ??
    'http://localhost:5174'
  return `${taban}/?t=${encodeURIComponent(jeton)}`
}

/** Veli portalının adresi — oynatıcıyla aynı mantık */
export function veliAdresi(jeton: string): string {
  const taban =
    (import.meta.env['VITE_VELI'] as string | undefined) ??
    kardesAlan('veli') ??
    'http://localhost:5176'
  return `${taban}/?g=${encodeURIComponent(jeton)}`
}

/**
 * WhatsApp'ta paylaşma bağlantısı.
 *
 * Mesajı BİZ göndermiyoruz: uygulayıcının kendi WhatsApp'ı açılıyor,
 * metin hazır geliyor, göndermeye o karar veriyor. WhatsApp Business
 * API'si Meta'da işletme doğrulaması istiyor — SMS başlığıyla aynı
 * evrak derdi. Bu yol hiçbir onay gerektirmiyor ve bugün çalışıyor.
 */
export function whatsappAdresi(telefon: string | null | undefined, metin: string): string {
  const rakam = (telefon ?? '').replace(/\D/g, '').replace(/^(90|0)/, '')
  const hedef = /^5\d{9}$/.test(rakam) ? `90${rakam}` : ''
  return `https://wa.me/${hedef}?text=${encodeURIComponent(metin)}`
}

export const ROL_ADI: Record<string, string> = {
  owner: 'Merkez sahibi',
  practitioner: 'Psikolog',
  assistant: 'Sekreter',
  trainee: 'Stajyer',
}
